import React, { useCallback, useState } from 'react'
import { StyleSheet } from 'react-native'
import { v4 as uuidv4 } from 'uuid'

import { ImageContainer } from 'partials/EditableAvatar/styled'
import { UploadButton } from 'partials/UploadButton'

import { baseCrop, defaultAvatarImage } from 'widgets/Avatar/defaultAvatarImage'
import { Box } from 'widgets/Box'
import { Icon } from 'widgets/Icon'
import { Image } from 'widgets/Image'
import { ImageCropper } from 'widgets/ImageCropper'
import { Modal } from 'widgets/Modal'
import { Pressable } from 'widgets/Pressable'

import { useCoverUpload } from 'hooks/useCoverUpload'
import { useCurrentUser } from 'hooks/useCurrentUser'
import { useFileSelect } from 'hooks/useFileSelect'
import { useGroup } from 'hooks/useGroup'
import { useProgram } from 'hooks/useProgram'

import commonStyles from 'utils/styles/commonStyles'

import { SelectedFile } from 'types/file'
import { Media, MediaCrop } from 'types/media'
import { ProgramType } from 'types/program'

import { ImageUploadButtonProps } from './ImageUploadButton'

const styles = StyleSheet.create({
  cameraIconContainer: {
    borderRadius: 5,
    overflow: 'hidden',
  },
})

export const ImageUploadButton = ({ programId }: ImageUploadButtonProps) => {
  const { data: currentUser } = useCurrentUser()
  const { data: group, updateGroup } = useGroup({ userId: currentUser?.id })
  const { data: program, updateProgram } = useProgram(programId)

  const updateCover = programId ? updateProgram : updateGroup
  const updateData = programId ? program : group

  const { handleFileSelect, handleReset, selectedFile, selectedFileUrl } = useFileSelect()

  const type = programId ? ProgramType.PROGRAM : ProgramType.GROUP
  const { mutateAsync: uploadCover } = useCoverUpload(type)

  const [isSaving, setIsSaving] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [crop, setCrop] = useState<MediaCrop>(baseCrop)

  const onCropChange = (newCrop: MediaCrop) => setCrop(newCrop)

  const handleClose = useCallback(() => {
    if (isSaving) {
      return
    }

    setIsEditing(false)
    setCrop(baseCrop)
    handleReset()
  }, [handleReset, isSaving])

  const uploadCoverToProgram = useCallback(
    async (file: SelectedFile | Media) => {
      if (!updateData?.id) return
      setIsSaving(true)

      const uploadedCover = await uploadCover({
        data: {
          crop,
          file,
          programId: updateData.id,
        },
      })

      updateCover.mutate({
        ...updateData,
        cover: uploadedCover,
      })

      handleClose()
      setIsSaving(false)
    },
    [crop, handleClose, updateCover, uploadCover, updateData],
  )

  const handleSave = useCallback(() => {
    if (selectedFile) {
      uploadCoverToProgram(selectedFile || defaultAvatarImage)
    }
  }, [selectedFile, uploadCoverToProgram])

  return (
    <Box
      testID='ImageUploadIconInput'
      margin={{ bottom: 'md' }}
      align='center'
      flexDirection='row'
      justifyContent='center'
    >
      {updateData?.cover && (
        <Box
          testID='CoverImageContainer'
          align='center'
          height={100}
          width={100}
          style={styles.cameraIconContainer}
        >
          <Image
            cacheKey={
              updateData
                ? // eslint-disable-next-line no-underscore-dangle
                  `${updateData?.id}-${updateData}-cover-${updateData?.cover?._original?.etag}-small`
                : uuidv4()
            }
            imageWidth={160}
            resizeMode='cover'
            source={{ uri: updateData?.cover.url }}
            style={commonStyles.fullSize}
          />
        </Box>
      )}

      {!updateData?.cover && (
        <Pressable>
          <Box
            testID='CameraIconContainer'
            align='center'
            style={styles.cameraIconContainer}
            height={100}
            width={100}
            border={{ width: 2, color: type === ProgramType.GROUP ? 'v2accent' : 'greyLight' }}
            justifyContent='center'
          >
            <Icon
              name='camera'
              color={type === ProgramType.GROUP ? 'v2accent' : 'greyLight'}
              size={32}
            />
          </Box>
          <Box position='absolute' height='100%' width='100%'>
            <UploadButton
              fileType='image'
              onFileSelect={(file: any) => {
                setIsEditing(true)
                handleFileSelect(file)
              }}
            />
          </Box>
        </Pressable>
      )}

      <Modal
        actionButtonProps={[
          {
            disabled: isSaving,
            onPress: handleClose,
            title: 'Cancel',
            type: 'v2primary',
          },
          {
            disabled: !selectedFile || isSaving,
            onPress: handleSave,
            title: 'Save',
            type: 'v2primary',
          },
        ]}
        isVisible={isEditing}
        onClose={handleClose}
        title='Edit Your Cover Image'
      >
        <ImageContainer
          flex={1}
          margin={{ vertical: 'xl' }}
          align='center'
          justifyContent='space-between'
        >
          {selectedFileUrl ? (
            <ImageCropper src={selectedFileUrl} cropSettings={crop} onChange={onCropChange} />
          ) : null}
        </ImageContainer>
      </Modal>
    </Box>
  )
}
